<script setup>
import { onMounted, onUnmounted, ref, useRoute } from '#imports';
import { useMenu } from '~/composables/use-menu';
import { useClickOutside } from '~/helpers/use-click-outside';

const props = defineProps({
  tags: {
    type: Array,
    default: () => [],
    required: true,
  },
});

const emit = defineEmits(['close', 'fetchTag', 'search']);
const currentRoute = useRoute();
const searchInput = ref();
const isInputFocused = ref(false);
const menuReference = ref(null);
const input = ref(null);

const { isSubMenuOpen, toggleSubMenu } = useMenu();

const backToTopLevel = () => {
  toggleSubMenu();
};

const getRoute = (targetRoute) =>
  currentRoute.path === targetRoute ? undefined : targetRoute;

const handleCloseMenu = () => {
  emit('close');
  toggleSubMenu();
};

const handleOpenSearch = () => {
  input.value[0].focus();
  isInputFocused.value = true;
};

const handleSearch = () => {
  if (searchInput.value) {
    emit('search', searchInput.value);
    handleCloseMenu();
  }
};

const handleOpenTag = (tag) => {
  emit('fetchTag', tag);
  handleCloseMenu();
};

useClickOutside(menuReference, () => {
  handleCloseMenu();
});

const handleOpenExclusive = () => {
  emit('fetchTag', 'Эксклюзив DOXA');
  handleCloseMenu();
};

const menuActions = {
  search: { action: handleOpenSearch, text: 'Найти' },
  exclusive: { action: handleOpenExclusive, text: 'Эксклюзивы' },
  tags: { action: toggleSubMenu, text: 'По тегам' },
};

const handleClick = (action) => {
  if (action) {
    action();
  }
};

onMounted(() => {
  if (CSS.supports('overflow', 'clip')) {
    document.documentElement.style.overflow = 'hidden';
  }
});

onUnmounted(() => {
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
});
</script>

<template>
  <nav ref="menuReference" class="menu">
    <ul v-show="!isSubMenuOpen" class="menu__main">
      <li
        v-for="(action, name) in menuActions"
        :key="name"
        class="menu__item"
        :class="{
          search: name === 'search',
        }"
      >
        <template v-if="name === 'search'">
          <div class="search">
            <input
              ref="input"
              v-model="searchInput"
              class="search__input"
              type="search"
              placeholder="ищу"
              @keyup.enter="handleSearch"
            />
            <button
              class="search__button"
              v-on="
                searchInput
                  ? { click: handleSearch }
                  : { click: handleOpenSearch }
              "
            >
              Найти
            </button>
          </div>
        </template>

        <template v-else>
          <a
            :href="getRoute(action.route)"
            class="menu__item-link"
            @click="handleClick(action.action)"
          >
            {{ action.text }}
          </a>
        </template>
      </li>
    </ul>

    <ul v-show="isSubMenuOpen" ref="subMenuReference" class="menu__submenu">
      <li class="menu__item" @click="backToTopLevel">
        <BackButton />
      </li>
      <li v-for="tag in props.tags" :key="tag.slug" class="menu__item">
        <div
          v-if="tag.name"
          class="menu__item-link"
          @click="handleOpenTag(tag.name)"
        >
          {{ tag.name }}
        </div>
        <span v-else>
          {{ tag.name }}
        </span>
      </li>
    </ul>
  </nav>
</template>

<style lang="postcss" scoped>
.menu {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  input,
  button {
    font-family: var(--title-font-family);
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    transition: var(--default-transition) background-color;
    color: #fffafd;
    background-color: var(--accent-color);
    border: none;
    padding: 8px 16px;
    margin: 0;
  }

  &__item {
    font-family: var(--title-font-family);
    font-size: var(--body-font-size);
    line-height: var(--body-line-height);
    transition: var(--default-transition) background-color;
    color: #fffafd;
    background-color: var(--accent-color);
    width: fit-content;
    border-radius: 20px;
    letter-spacing: 1px;
    font-weight: 500;

    &:focus,
    &:active {
      outline: none;
      background-color: var(--accent-color);
      -webkit-tap-highlight-color: transparent;
    }

    button {
      font-weight: 500;
    }

    @media (--desktop-vp) {
      &:hover {
        background-color: #635bff;
      }
    }

    @media (--small-vp) {
      &:first-child {
        display: block;
      }
    }
  }

  &__item-link {
    display: inline-flex;
    padding: 8px 16px;

    &--hidden {
      display: none;
    }

    a:focus-visible {
      outline: none;
    }
  }

  .search {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    background-color: transparent;
    position: relative;
    border-radius: 20px;

    input[type='search']::-webkit-search-cancel-button {
      -webkit-appearance: none;
    }

    &__button {
      border-radius: 20px;
      position: absolute;
      right: 0;
    }

    &__input:focus + button {
      background-color: var(--green-color);
      color: var(--accent-color);
    }

    &__input {
      width: 40px;
      border-radius: 20px;
      color: var(--accent-color);

      &:focus {
        outline: none;
        transition: width 0.3s ease-in-out;
        width: 260px;
        max-width: 300px;
        padding-right: 120px;
        box-shadow: 0 0 0 2px var(--accent-color);
        background-color: #fffafd;
      }
    }
  }

  &__main {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    gap: 6px;

    &::before {
      position: absolute;
      z-index: -1;
      top: 50%;
      left: 50%;
      width: 490px;
      height: 490px;
      content: '';
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background: radial-gradient(
        50% 50% at 50% 50%,
        #fff 0,
        rgba(255, 255, 255, 80%) 60%,
        rgba(255, 255, 255, 0%) 100%
      );
    }
  }

  &__submenu {
    overflow-y: scroll;
    max-height: calc(100vh - 192px);
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    margin-top: 10px;
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    gap: 6px;

    &::before {
      position: absolute;
      z-index: -1;
      bottom: -85%;
      left: 50%;
      width: 490px;
      height: 490px;
      content: '';
      transform: translate(-50%, -50%);
      border-radius: 50%;
      background: radial-gradient(
        50% 50% at 50% 50%,
        #fff 0,
        rgba(255, 255, 255, 80%) 60%,
        rgba(255, 255, 255, 0%) 100%
      );
    }

    &::-webkit-scrollbar {
      /* Chrome, Safari and Opera */
      display: none;
    }
  }

  @media (--small-vp) {
    &__item-link {
      padding: 8px 16px;
      max-width: 320px;
    }
  }
}
</style>
